/*!
 * AquaVitales/Camping-Schorni Produktkompass — Entscheidungs-Gruppen
 * -----------------------------------------------------------------------------
 * Baut aus dem Recommender-Ergebnis + data/alternativen.json umschaltbare
 * Gruppen fuer die Ergebnisseite: "Einzelartikel oder Hygiene-Trio?",
 * "Mobile Edition, WM Filter oder FIE-100?", "UV einzeln oder KLW?" plus
 * UV-Modell-Umschalter (Kundenwahl, Entscheidung Ulrike 16.07.2026).
 * Waehlt eine Option mit "deckt_ab" (z. B. KLW), werden die abgedeckten
 * Gruppen ausgegraut und aus der Summe genommen — loest das KLW-Dedup-Problem.
 * Konzept Ulrike 01.08.2026, Umsetzung Claude. Laeuft in Browser UND Node.
 * v2.8 (13.09.2026): nur_wenn_kontext, Komplett-Set nie x2, eco/steel-Wahl bei KLW, keine stille Verkleinerung.
 * v2.9 (14.09.2026): KLW ersetzt den Kuechen-Aktivkohlefilter (ersetzt_skus), bevorzugte Vorauswahl, Preisvergleich je Option.
 * v2.12 (21.09.2026): Gruppen mit nur_wenn, Optionen aus mehreren Artikeln (10-Zoll-Befuellfilter), Trio-Kombination bei
 *   getrennten Tanks (Trio nach Gesamtvolumen + passende Zusatznetze vs. Trio je Tank — das Guenstigere), Home Edition.
 * v2.12 (08.10.2026): Optionale Gruppen — standard.leer = Gruppe auch ohne Artikel in der Empfehlung (Vorauswahl "Habe ich schon",
 *   nichts im Warenkorb), erklaerung/erklaerung_du (+ erklaerung_mobil_du beim UV-Koffer) als Gruppentext, nur_wenn_oder
 *   (Befuellschlaeuche: UV-Koffer ODER Tankbetrieb).
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.AVPKAlternativen = factory();
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function phpFloat(v) {
    if (v === null || v === undefined || v === false) return 0;
    if (typeof v === 'number') return isFinite(v) ? v : 0;
    var m = String(v).trim().match(/^[+-]?(\d+(\.\d*)?|\.\d+)/);
    return m ? parseFloat(m[0]) : 0;
  }

  // v2.8 (13.09.2026): Bedingungen koennen jetzt auch {in: [...]}, {min: n}, {max: n} tragen —
  // gebraucht, um Trio/DEXDA One/KLW nur bei Tankbetrieb anzubieten (Pruefbericht logik-qa-08).
  function bedingungErfuellt(nurWenn, werte) {
    if (!nurWenn) return true;
    werte = werte || {};
    for (var k in nurWenn) {
      if (!Object.prototype.hasOwnProperty.call(nurWenn, k)) continue;
      var soll = nurWenn[k], ist = werte[k];
      if (soll !== null && typeof soll === 'object' && !Array.isArray(soll)) {
        if (soll.in !== undefined && [].concat(soll.in).indexOf(ist) === -1) return false;
        if (soll.min !== undefined && phpFloat(ist) < phpFloat(soll.min)) return false;
        if (soll.max !== undefined && phpFloat(ist) > phpFloat(soll.max)) return false;
      } else if (ist !== soll) {
        return false;
      }
    }
    return true;
  }

  // Minimale Familienauswahl — gleiches Verhalten wie der Recommender.
  function skuAusFamilie(add, catalog, ctx) {
    if (add.skus && add.skus.length) return add.skus[0];
    var cands = (catalog.products || []).filter(function (p) {
      return (p.kategorie || '') === add.sku_family && phpFloat(p.tank_max_liter) > 0;
    });
    if (!cands.length) return null;
    cands.sort(function (a, b) { return phpFloat(a.tank_max_liter) - phpFloat(b.tank_max_liter); });
    var needed = phpFloat((ctx || {}).tankvolumen_total);
    // Gleiche Groessen-Toleranz wie im Recommender (rules.json formulas.groessen_toleranz):
    // ein 170-Liter-Tank bekommt das 160er-Set, nicht die naechstgroessere Stufe.
    var karte = (ctx || {})._groessen_toleranz_je_kategorie || {};
    var toleranz = phpFloat(karte[add.sku_family]) || 1.0;
    for (var i = 0; i < cands.length; i++) {
      if (phpFloat(cands[i].tank_max_liter) * toleranz >= needed) return cands[i].sku;
    }
    // v2.8 (13.09.2026): Reicht auch das groesste Gebinde nicht, wird die Option
    // ausgeblendet statt still ein zu kleines Set zu empfehlen (Pruefbericht shop-abgleich-09).
    return null;
  }

  function alsItem(sku, catalog, qty, reason) {
    var p = (catalog.products || []).find(function (x) { return x.sku === sku; });
    if (!p) return null;
    return {
      sku: p.sku, name: p.name, qty: qty || 1,
      einheit: p.einheit || 'Stk',
      preis: p.preis === undefined ? null : p.preis,
      bild: p.bild || '', kategorie: p.kategorie,
      marke: p.marke !== undefined ? p.marke : null,
      system: null, wc_product_id: p.wc_product_id || null,
      reason: reason || p.kurz || '',
      _alternative: true
    };
  }

  /**
   * @param result   Recommender-Ergebnis ({items, context, ...})
   * @param def      data/alternativen.json
   * @param catalog  data/products.json
   * @param answers  Roh-Antworten (fuer nur_wenn)
   * @param wahl     { <gruppeId>: <optionId>, uv_modell: <sku> }
   * @param anrede   'du' | 'sie'
   * @return { gruppen, material, aktiveItems }
   */
  function aufbereiten(result, def, catalog, answers, wahl, anrede) {
    wahl = wahl || {};
    var ctx = result.context || {};
    var zugeordnet = {};
    var gruppen = [];

    (def.gruppen || []).forEach(function (g) {
      // v2.12: Gruppe nur unter Bedingung (Kuechenfilter-Gruppe nur bei Sterilfiltration) —
      // sonst bleiben die Artikel in der Restliste und die KLW-Ersetzung greift wie bisher.
      // v2.12 (08.10.2026): nur_wenn_oder = Liste von Bedingungen, EINE genuegt (Befuellschlauch: UV-Koffer ODER Tankbetrieb)
      if (g.nur_wenn_oder) {
        if (!g.nur_wenn_oder.some(function (c) { return bedingungErfuellt(c, answers); })) return;
      } else if (!bedingungErfuellt(g.nur_wenn, answers)) return;
      var standardKats = (g.standard && g.standard.aus_kategorien) || [];
      var standardItems = result.items.filter(function (it) {
        return standardKats.indexOf(it.kategorie) !== -1;
      });
      standardItems.forEach(function (it) { zugeordnet[it.sku] = true; });
      if (!standardItems.length && !(g.standard && g.standard.leer)) return; // Gruppe ohne Anlass (z. B. kein Vorfilter gewaehlt)

      var optionen = [{
        id: g.standard.id, label: g.standard.label,
        items: standardItems, deckt_ab: [], hinweis: ''
      }];

      (g.alternativen || []).forEach(function (a) {
        if (!bedingungErfuellt(a.nur_wenn, answers)) return;
        if (!bedingungErfuellt(a.nur_wenn_kontext, ctx)) return;
        // v2.8: Kundenwahl der Ausstattung auch bei Komplett-Sets (eco/steel) — Familie je Wahl
        var add = a.add || {}, optionsModell = null, sku = null;
        if (a.modell_umschalter && a.modell_umschalter.familien) {
          // Abnahme 13.09. (logik-01): Liefert die gewaehlte Familie keine passende Groesse
          // (steel endet bei 320 L, darueber liegt nur die steel-KLW500 in der Standard-Leiter),
          // wird in den uebrigen Familien gesucht statt die Option zu verwerfen. Der Schalter
          // zeigt die Ausstattung, die das gefundene Set WIRKLICH hat, und verschwindet, wenn
          // beide Wege dasselbe Set liefern.
          var mu2 = a.modell_umschalter, keys = Object.keys(mu2.familien);
          var std = mu2.standard || keys[0];
          var gew = (wahl[a.id + '_modell'] && mu2.familien[wahl[a.id + '_modell']]) ? wahl[a.id + '_modell'] : std;
          var reihe = [gew].concat(keys.filter(function (k) { return k !== gew; }));
          var jeFamilie = {};
          keys.forEach(function (k) { jeFamilie[k] = skuAusFamilie({ sku_family: mu2.familien[k], select_by: add.select_by }, catalog, ctx); });
          for (var ri = 0; ri < reihe.length && !sku; ri++) sku = jeFamilie[reihe[ri]];
          if (!sku) return;
          var aktivKey = gew;
          keys.forEach(function (k) { if (jeFamilie[k] === sku && mu2.familien[k] === ((catalog.products || []).filter(function (x) { return x.sku === sku; })[0] || {}).kategorie) aktivKey = k; });
          if (/STEEL/.test(sku) && keys.indexOf('steel') !== -1) aktivKey = 'steel';
          var verschieden = keys.some(function (k) { return jeFamilie[k] && jeFamilie[k] !== sku; });
          optionsModell = verschieden ? { keys: keys, labels: mu2.labels || {}, aktiv: aktivKey, wahlfeld: a.id + '_modell' } : null;
        } else {
          sku = skuAusFamilie(add, catalog, ctx);
        }
        // v2.12 (Ulrike 21.09.2026): Getrennte Tanks — Reiniger/Entkalker decken das Gesamtvolumen,
        // jeder Tank braucht sein eigenes Netz. Zwei Wege, der Kompass nimmt den guenstigeren:
        //   A) ein Trio nach Gesamtvolumen (Netz in den groessten Tank) + je weiterem Tank ein passendes Netz
        //   B) ein Trio je Tank in dessen Groesse
        // Beispiele (Preise 21.09.): 2x80 l → HGT-120 + SIPA80 = 113,80 (B: 125,80); 2x150 l → 2x HGT-120 = 149,80 (A: 181,90).
        var trioKombi = null;
        if (a.trio_getrennte_tanks && phpFloat(ctx.anzahl_tanks) > 1 && answers.tanks_verbunden === 'nein') {
          var vol = (ctx.tanks || []).map(function (t) { return phpFloat(t.volumen); }).filter(function (v) { return v > 0; })
                                     .sort(function (x, y) { return y - x; });
          var trioFuer = function (liter) { return skuAusFamilie({ sku_family: add.sku_family }, catalog, { tankvolumen_total: liter }); };
          var netzFuer = function (liter) { return skuAusFamilie({ sku_family: a.trio_getrennte_tanks.netz_familie || 'tank_konservierung' }, catalog, { tankvolumen_total: liter }); };
          var preisVon = function (s) { var p = (catalog.products || []).filter(function (x) { return x.sku === s; })[0]; return p ? phpFloat(p.preis) : 0; };
          var wege = [];
          var trioA = trioFuer(ctx.tankvolumen_total), netzeA = vol.slice(1).map(netzFuer);
          if (trioA && netzeA.every(Boolean)) wege.push({ skus: [trioA].concat(netzeA), art: 'kombi' });
          var triosB = vol.map(trioFuer);
          if (triosB.every(Boolean)) wege.push({ skus: triosB, art: 'je_tank' });
          if (!wege.length) return;
          wege.forEach(function (w) { w.summe = w.skus.reduce(function (sum, s) { return sum + preisVon(s); }, 0); });
          wege.sort(function (x, y) { return x.summe - y.summe; });
          trioKombi = wege[0];
          sku = trioKombi.skus[0];
        }
        if (!sku) return;
        // v2.12 (Justus-Auflage 08.10.2026): Eine Option mit einem Artikel, den es im Shop (noch) nicht gibt (products.json shop_offen),
        // wird nicht angeboten — keine Preise fuer Ware, die man nicht kaufen kann. Das Flag zu entfernen schaltet die Option wieder ein.
        if ((add.skus || []).some(function (s) { var pr = (catalog.products || []).filter(function (x) { return x.sku === s; })[0]; return pr && pr.shop_offen; })) return;
        // v2.12: Optionen aus mehreren Artikeln (z. B. 10-Zoll-Gehaeuse + Aktivkohle-Element)
        var mehrSkus = (add.skus && add.skus.length > 1) ? add.skus.slice() : null;
        if (standardItems.some(function (it) { return it.sku === sku; })) {
          // Standard IST diese Option: Label uebernehmen ("Mobile Edition (empfohlen)"), Abnahme template-11
          if (standardItems.length === 1) optionen[0].label = a.label + ' (empfohlen)';
          return;
        }
        var qty = standardItems.length === 1 ? standardItems[0].qty : 1;
        var hinweis = (anrede === 'du' && a.hinweis_du) || a.hinweis || '';
        if (a.modell_umschalter && optionsModell === null && /STEEL/.test(sku)) {
          hinweis += ' Ab 321 Liter gibt es die Komplett-Lösung nur in der steel-Ausführung.';
        }
        // v29-02: getrennte Tanks brauchen je ein Silbernetz — das Set bringt eines mit
        if (!trioKombi && (a.deckt_ab || []).indexOf('tank_hygiene') !== -1 && phpFloat(ctx.anzahl_tanks) > 1 && answers.tanks_verbunden === 'nein') {
          hinweis += ' Bei getrennten Tanks braucht jeder Tank ein eigenes Silbernetz — das Set enthält eines.';
        }
        // v2.8 (13.09.2026): Ein Komplett-Set (KLW) wird nie x2 empfohlen — sonst verdoppelt sich
        // die Chemie. Die zweite UV-Einheit kommt als Einzelartikel dazu (Pruefbericht logik-qa-07).
        var zusatz = [];
        var basisKat = (catalog.products || []).filter(function (x) { return x.sku === sku; }).map(function (x) { return x.kategorie; })[0] || '';
        if (qty > 1 && /^uv_komplett/.test(basisKat)) {
          var einzel = standardItems.length === 1 ? standardItems[0] : null;
          if (einzel) {
            var zweite = alsItem(einzel.sku, catalog, qty - 1,
              anrede === 'du' ? 'Zweite UV-C-Einheit als Einzelgerät — die Komplett-Lösung enthält eine.'
                              : 'Zweite UV-C-Einheit als Einzelgerät — die Komplett-Lösung enthält eine.');
            if (zweite) zusatz.push(zweite);
          }
          qty = 1;
        }
        if (trioKombi) {
          var trioTexte = a.trio_getrennte_tanks;
          hinweis = (anrede === 'du' && trioTexte[trioKombi.art + '_du']) || trioTexte[trioKombi.art] || hinweis;
        }
        var item = alsItem(sku, catalog, qty, hinweis);
        if (!item) return;
        // v2.12: Zusatzartikel der Option — Trio-Kombination (Zusatznetze / weitere Trios) oder Mehrfach-SKU
        var weitereSkus = trioKombi ? trioKombi.skus.slice(1) : (mehrSkus ? mehrSkus.slice(1) : []);
        var gezaehlt = {};
        weitereSkus.forEach(function (s) { gezaehlt[s] = (gezaehlt[s] || 0) + 1; });
        Object.keys(gezaehlt).forEach(function (s) {
          if (s === sku) { item.qty += gezaehlt[s]; return; }
          var z = alsItem(s, catalog, gezaehlt[s], hinweis);
          if (z) zusatz.push(z);
        });
        // v1.6 (Ulrike 03.08.2026): Eine Alternative kann die Gruppe GANZ ersetzen
        // (Hygiene-Trio deckt Reinigen, Entkalken und Konservieren ab) oder nur EINEN
        // Teil davon. "mit DEXDA One statt Silbernetz" tauscht nur die Konservierung —
        // Reiniger und Entkalker mussten bisher mit verschwinden, das war falsch.
        // Ohne das Feld bleibt das alte Verhalten: die Alternative ersetzt alles.
        var ersetzt = a.ersetzt_kategorien || null;
        var eigeneItems = [item].concat(zusatz);
        if (ersetzt && ersetzt.length) {
          var behalten = standardItems.filter(function (it) {
            return ersetzt.indexOf(it.kategorie) === -1;
          });
          eigeneItems = [item].concat(zusatz, behalten);
        }
        optionen.push({ id: a.id, label: a.label, items: eigeneItems, deckt_ab: a.deckt_ab || [], hinweis: hinweis,
                        modell_umschalter: optionsModell, ersetzt_skus: a.ersetzt_skus || [],
                        bevorzugt: !!a.bevorzugt && bedingungErfuellt(a.bevorzugt_wenn, answers)
                          && !(a.bevorzugt_nicht_wenn && bedingungErfuellt(a.bevorzugt_nicht_wenn, answers))
                          && !(a.bevorzugt_nicht_wenn_2 && bedingungErfuellt(a.bevorzugt_nicht_wenn_2, answers)),
                        bevorzugt_bis_differenz_eur: a.bevorzugt_bis_differenz_eur !== undefined ? phpFloat(a.bevorzugt_bis_differenz_eur) : 0,
                        preisvergleich: !!a.preisvergleich });
      });

      // Befuellfilter-Muster: feste Optionsliste, Empfehlung vorausgewaehlt.
      // Die Standard-Option bekommt als Label den Produktnamen, wenn sie 1 Artikel hat
      // und es Alternativen mit eigenen Labels gibt.
      // v2.9-Abnahme (v29-01): Auch eine Ausstattungswahl (eco/act/safe/steel bzw. KLW eco/steel) ist eine
      // bewusste Entscheidung fuer diese Gruppe — sie darf die Vorauswahl nicht mehr kippen.
      var gruppenWahl = !!(wahl[g.id] && optionen.some(function (o) { return o.id === wahl[g.id]; }));
      var wahlExplizit = gruppenWahl
        || !!(g.modell_umschalter && wahl.uv_modell)
        || optionen.some(function (o) { return o.modell_umschalter && wahl[o.modell_umschalter.wahlfeld]; });
      var aktivId = gruppenWahl ? wahl[g.id] : optionen[0].id;

      gruppen.push({
        id: g.id, titel: g.titel, optionen: optionen, aktiv: aktivId, wahl_explizit: wahlExplizit,
        modell_umschalter: null, abgedeckt_von: null,
        erklaerung: ((answers.uv_einbau === 'mobil' && anrede === 'du' && g.erklaerung_mobil_du) || (anrede === 'du' && g.erklaerung_du) || g.erklaerung || '')
      });

      // UV-Modell-Umschalter (nur auf der Standard-Option "einzeln")
      if (g.modell_umschalter && bedingungErfuellt(g.modell_umschalter.nur_wenn, answers)) {
        var mu = g.modell_umschalter;
        var basisItem = standardItems.find(function (it) { return mu.skus.indexOf(it.sku) !== -1; });
        if (basisItem) {
          var aktuelleGruppe = gruppen[gruppen.length - 1];
          aktuelleGruppe.modell_umschalter = {
            skus: mu.skus, labels: mu.labels || {},
            aktiv: (wahl.uv_modell && mu.skus.indexOf(wahl.uv_modell) !== -1) ? wahl.uv_modell : basisItem.sku
          };
          if (aktivId === g.standard.id && aktuelleGruppe.modell_umschalter.aktiv !== basisItem.sku) {
            var ersatz = alsItem(aktuelleGruppe.modell_umschalter.aktiv, catalog, basisItem.qty, basisItem.reason);
            if (ersatz) {
              ersatz._alternative = false;
              aktuelleGruppe.optionen[0] = {
                id: g.standard.id, label: g.standard.label,
                items: standardItems.map(function (it) { return it === basisItem ? ersatz : it; }),
                deckt_ab: [], hinweis: ''
              };
            }
          }
        }
      }
    });

    function summe(items) {
      return (items || []).reduce(function (s, i) { return s + phpFloat(i.preis) * (phpFloat(i.qty) || 1); }, 0);
    }
    function aktiveOption(g) { return g.optionen.find(function (o) { return o.id === g.aktiv; }); }

    // v2.9: Preisvergleich je Option — was ersetzt sie (eigene Gruppe, abgedeckte Gruppen,
    // ersetzte Einzelartikel) und was kostet sie? differenz > 0 = teurer als die Einzelteile.
    gruppen.forEach(function (g) {
      var standard = g.optionen[0];
      g.optionen.forEach(function (o) {
        if (!o.preisvergleich) return;
        var ersetzt = standard.items.slice();
        (o.deckt_ab || []).forEach(function (zielId) {
          gruppen.forEach(function (z) { if (z.id === zielId) ersetzt = ersetzt.concat(aktiveOption(z).items); });
        });
        (o.ersetzt_skus || []).forEach(function (sku) {
          result.items.forEach(function (it) { if (it.sku === sku) ersetzt.push(it); });
        });
        o.differenz = Math.round((summe(o.items) - summe(ersetzt)) * 100) / 100;
        // v2.9 (Ulrike 14.09.2026): Set als Vorauswahl nur, wenn es die Einzelartikel wirklich ersetzt
        // (der Kuechen-Aktivkohlefilter ist in der Empfehlung) und hoechstens bevorzugt_bis_differenz_eur
        // teurer ist als die Einzelteile — sonst bleiben die Einzelartikel vorne, das Set als Umschalter.
        var ersetztVorhanden = (o.ersetzt_skus || []).every(function (sku) { return result.items.some(function (it) { return it.sku === sku; }); });
        o.bevorzugt = !!o.bevorzugt && ersetztVorhanden && o.differenz <= o.bevorzugt_bis_differenz_eur;
      });
      if (!g.wahl_explizit) {
        var bevorzugte = g.optionen.filter(function (o) { return o.bevorzugt; })[0];
        if (bevorzugte) g.aktiv = bevorzugte.id;
      }
      // v2.9 (Ulrike 14.09.2026): dem Kunden sagen, WARUM Einzelartikel oder Set vorne stehen
      g.preishinweis = null;
      var defTexte = (def.gruppen.find(function (x) { return x.id === g.id; }) || {}).preishinweis;
      var vgl = g.optionen.filter(function (o) { return typeof o.differenz === 'number'; })[0];
      // Abnahme v29-01a: Hat der Kunde beim Einzelgeraet eine andere Ausstattung gewaehlt (act/safe/steel),
      // vergleicht die Differenz eco-Set gegen ein anderes Geraet — das ist kein fairer Vergleich mehr.
      var modellGewaehlt = !!(g.modell_umschalter && g.modell_umschalter.aktiv && g.modell_umschalter.aktiv !== g.modell_umschalter.skus[0]);
      if (modellGewaehlt) {
        g.optionen.forEach(function (o) { delete o.differenz; });
      } else if (defTexte && vgl) {
        // Abnahme v29-G1: Text nach dem TATSAECHLICHEN Zustand — "deshalb vorausgewaehlt" nur, wenn das Set aktiv ist.
        var betrag = Math.abs(vgl.differenz).toFixed(2).replace('.', ',') + ' €';
        var setAktiv = g.aktiv === vgl.id;
        var key = vgl.differenz < 0 ? (setAktiv ? 'set_guenstiger' : 'set_guenstiger_nicht_vorne')
                : vgl.differenz > 0 ? (setAktiv ? 'set_gewaehlt' : 'einzeln_guenstiger')
                : 'gleich';
        if (defTexte[key]) g.preishinweis = String(defTexte[key]).replace('{betrag}', betrag);
      }
    });

    // deckt_ab auswerten: aktive Optionen koennen andere Gruppen abdecken
    var abgedeckteItems = [];
    gruppen.forEach(function (g) {
      var aktiv = aktiveOption(g);
      (aktiv && aktiv.deckt_ab || []).forEach(function (zielId) {
        gruppen.forEach(function (z) {
          if (z.id === zielId) z.abgedeckt_von = g.titel + ' — ' + aktiv.label;
        });
      });
      // v2.9: einzelne Artikel, die im gewaehlten Set schon stecken (KLW: Kuechen-Aktivkohlefilter)
      (aktiv && aktiv.ersetzt_skus || []).forEach(function (sku) {
        result.items.forEach(function (it) {
          if (it.sku === sku && !zugeordnet[it.sku]) {
            zugeordnet[it.sku] = true;
            abgedeckteItems.push({ sku: it.sku, name: it.name, von: g.titel + ' — ' + aktiv.label });
          }
        });
      });
    });

    var material = result.items.filter(function (it) {
      return ['schlauch', 'fitting'].indexOf(it.kategorie) !== -1;
    });
    material.forEach(function (it) { zugeordnet[it.sku] = true; });
    var rest = result.items.filter(function (it) { return !zugeordnet[it.sku]; });

    var aktiveItems = [];
    gruppen.forEach(function (g) {
      if (g.abgedeckt_von) return;
      var aktiv = g.optionen.find(function (o) { return o.id === g.aktiv; });
      if (aktiv) aktiveItems = aktiveItems.concat(aktiv.items);
    });
    aktiveItems = aktiveItems.concat(rest);

    return { gruppen: gruppen, rest: rest, material: material, aktiveItems: aktiveItems, abgedeckteItems: abgedeckteItems };
  }

  return { aufbereiten: aufbereiten, _skuAusFamilie: skuAusFamilie };
}));
